import React, { useEffect, useRef, useState } from 'react';
import { debounce, setOnce } from '@/features/services/media/hooks/stateGuards';

export interface MasterFrequencyResponseProps {
  analyser: AnalyserNode | null;
  filter?: BiquadFilterNode | null;
  highpass?: BiquadFilterNode | null;
  eqEnabled?: boolean;
  eqHpfEnabled?: boolean;
  eqLpfEnabled?: boolean;
  width?: number;
  height?: number;
  fps?: number; // render rate
  logScale?: boolean; // log-frequency axis
  minDb?: number; // for normalization
  maxDb?: number; // for normalization
  className?: string;
  cutoffHz?: number; // low-pass cutoff to display
  onCutoffChange?: (hz: number) => void; // drag handler to update LPF cutoff
  hpfHz?: number; // high-pass cutoff to display
  onHpfChange?: (hz: number) => void; // drag handler to update HPF cutoff
  status?: 'idle' | 'loading' | 'playing' | 'paused';
  background?: string; // canvas background fill
}

const MasterFrequencyResponse: React.FC<MasterFrequencyResponseProps> = ({
  analyser,
  width = 600,
  height = 160,
  fps = 30,
  logScale = true,
  minDb = -90,
  maxDb = -30,
  className,
  filter,
  highpass,
  eqEnabled = true,
  eqHpfEnabled = true,
  eqLpfEnabled = true,
  cutoffHz,
  onCutoffChange,
  hpfHz,
  onHpfChange,
  status,
  background = '#131313',
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const rafRef = useRef<number | null>(null);
  const lastTsRef = useRef<number>(0);
  const draggingRef = useRef<'lp' | 'hp' | false>(false);
  const workerRef = useRef<Worker | null>(null);
  const pixelSpectrumRef = useRef<Float32Array | null>(null);
  const pendingReqRef = useRef<boolean>(false);
  const [, setSpectrumError] = useState<string | null>(null);
  const setErrOnce = useRef(setOnce(setSpectrumError, 'worker-error')).current;
  // Track CSS pixel width and devicePixelRatio-aware backing store
  const needsResizeRef = useRef<boolean>(true);
  const dprRef = useRef<number>(typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !analyser) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const sizeCanvas = () => {
      const cssW = Math.max(1, Math.floor(canvas.clientWidth || width));
      const dpr = (typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1);
      dprRef.current = dpr;
      // Set backing store to device pixels, draw in CSS pixels by scaling context
      if (canvas.width !== Math.floor(cssW * dpr) || canvas.height !== Math.floor(height * dpr)) {
        canvas.width = Math.floor(cssW * dpr);
        canvas.height = Math.floor(height * dpr);
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.scale(dpr, dpr);
      }
    };
    sizeCanvas();

    // instantiate module worker from public to avoid MIME mis-detection in some servers
    try {
      const worker = new Worker('/workers/spectrum-worker.js', { type: 'module' });
      workerRef.current = worker;
      worker.onmessage = (ev: MessageEvent) => {
        const msg = ev.data;
        if (!msg) return;
        if (msg.type === 'spectrumReady' && msg.buffer) {
          pixelSpectrumRef.current = new Float32Array(msg.buffer);
          pendingReqRef.current = false;
        } else if (msg.type === 'error') {
          console.warn('spectrum worker reported error:', msg.message);
          pendingReqRef.current = false;
        } else if (msg.type === 'pong') {
          setSpectrumError(null);
        }
      };
      worker.onerror = debounce((err) => { console.error('[spectrum] worker error', err); setErrOnce(); pendingReqRef.current = false; }, 250);
      // Optional: catch message deserialization issues
      // @ts-ignore - not in older TS lib dom
      if (typeof worker.onmessageerror !== 'undefined') {
        // @ts-ignore
    worker.onmessageerror = debounce((e: MessageEvent) => { console.warn('[spectrum] messageerror', e); pendingReqRef.current = false; }, 250);
      }
    } catch (err) {
      // fall back to inline/no-worker behavior
      console.warn('Failed to start spectrum worker, continuing without worker', err);
    }
  try { workerRef.current?.postMessage({ type: 'ping' }); } catch {}

    // Configure analyser gently (do not change fftSize unless needed globally)
    try { analyser.minDecibels = Math.min(minDb, analyser.minDecibels); } catch {}
    try { analyser.maxDecibels = Math.max(maxDb, analyser.maxDecibels); } catch {}

  let data = new Float32Array(analyser.frequencyBinCount);
    const nyquist = (analyser.context?.sampleRate || 48000) / 2;
    const fMin = 20;
    const fMax = nyquist;



    const freqToX = (freq: number) => {
      const w = Math.max(1, Math.floor(canvasRef.current?.clientWidth || width));
      const clamped = Math.max(fMin, Math.min(fMax, freq));
      if (!logScale) return ((clamped - fMin) / (fMax - fMin)) * w;
      const lf = Math.log10(clamped);
      const lmin = Math.log10(fMin);
      const lmax = Math.log10(fMax);
      return ((lf - lmin) / (lmax - lmin)) * w;
    };

    const xToFreq = (x: number) => {
      const w = Math.max(1, Math.floor(canvasRef.current?.clientWidth || width));
      const clampedX = Math.max(0, Math.min(w, x));
      if (!logScale) {
        const frac = clampedX / w;
        return fMin + frac * (fMax - fMin);
      }
      const lmin = Math.log10(fMin);
      const lmax = Math.log10(fMax);
      const lf = lmin + (clampedX / w) * (lmax - lmin);
      return Math.pow(10, lf);
    };

    const norm = (db: number) => {
      const v = (db - minDb) / (maxDb - minDb);
      return Math.max(0, Math.min(1, v));
    };

    const draw = (ts: number) => {
      const elapsed = ts - lastTsRef.current;
      const interval = 1000 / fps;
      if (elapsed < interval) { rafRef.current = requestAnimationFrame(draw); return; }
      lastTsRef.current = ts;

      // Resize backing store if needed (handles container width or DPR changes)
      if (needsResizeRef.current) { sizeCanvas(); needsResizeRef.current = false; }

      // Reallocate buffer if fftSize (frequencyBinCount) changed
      const binCount = analyser.frequencyBinCount;
      if (data.length !== binCount) {
        data = new Float32Array(binCount);
      }
      // If stopped (idle), clear visuals instead of freezing last frame
      if (status === 'idle') {
        // Transparent fills don't clear prior pixels; clear explicitly first.
        const transparentBg = !background || background === 'transparent' || background === 'rgba(0,0,0,0)';
        ctx.clearRect(0, 0, width, height);
        if (!transparentBg) {
          ctx.fillStyle = background;
          ctx.fillRect(0, 0, width, height);
        }
        // faint grid
        ctx.strokeStyle = 'rgba(255,255,255,0.06)';
        ctx.lineWidth = 1;
        for (let i = 1; i < 4; i++) { const y = (i / 4) * height; ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(width, y); ctx.stroke(); }
        // Do not draw spectrum when stopped. Allow handles/curves below to render for UX.
      } else {
        analyser.getFloatFrequencyData(data);
      }

  const W = Math.max(1, Math.floor(canvas.clientWidth || width));

      // background (transparent requires clearRect to avoid frame trails)
  const transparentBg = !background || background === 'transparent' || background === 'rgba(0,0,0,0)';
  ctx.clearRect(0, 0, W, height);
  if (!transparentBg) {
    ctx.fillStyle = background;
    ctx.fillRect(0, 0, W, height);
  }

      // grid lines (optional, light)
      ctx.strokeStyle = 'rgba(255,255,255,0.06)';
      ctx.lineWidth = 1;
      for (let i = 1; i < 4; i++) {
        const y = (i / 4) * height;
  ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke();
      }

      // vertical log-frequency grid (Hz)
      if (logScale) {
        const ticks = [20, 50, 100, 200, 500, 1000, 2000, 5000, 10000, 20000]
          .filter(f => f >= fMin && f <= fMax);
        ctx.save();
        ctx.strokeStyle = 'rgba(255,255,255,0.06)';
        ctx.fillStyle = 'rgba(255,255,255,0.35)';
        ctx.font = '10px ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'bottom';
        for (const f of ticks) {
          const x = freqToX(f) + 0.5; // align to pixel
          ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, height); ctx.stroke();
          const label = f >= 1000 ? `${(f / 1000).toFixed(f % 1000 === 0 ? 0 : 1)}k` : `${f}`;
          // Avoid drawing labels too close to edges
          if (x > 12 && x < W - 12) ctx.fillText(label, x, height - 2);
        }
        ctx.restore();
      }

      // response curve: skip when stopped
      if (status !== 'idle') {
        const pixels = pixelSpectrumRef.current;
        if (pixels && pixels.length >= W) {
          ctx.strokeStyle = '#9fffd8';
          ctx.lineWidth = 2;
          ctx.beginPath();
          let started = false;
          for (let x = 0; x < W; x++) {
            const db = pixels[x];
            const y = height * (1 - norm(db));
            if (!started) { ctx.moveTo(x, y); started = true; } else { ctx.lineTo(x, y); }
          }
          ctx.stroke();
          ctx.lineTo(W, height);
          ctx.lineTo(0, height);
          ctx.closePath();
          ctx.fillStyle = 'rgba(159,255,216,0.18)';
          ctx.fill();
        } else {
          // main-thread fallback: interpolate analyser bins per pixel
          ctx.strokeStyle = '#9fffd8';
          ctx.lineWidth = 2;
          ctx.beginPath();
          let started = false;
          for (let x = 0; x < W; x++) {
            const f = xToFreq(x);
            const pos = Math.max(0, Math.min((f / nyquist) * binCount, binCount - 1));
            const i0 = Math.floor(pos);
            const i1 = Math.min(binCount - 1, i0 + 1);
            const t = pos - i0;
            const db = (data[i0] ?? -100) * (1 - t) + (data[i1] ?? -100) * t;
            const y = height * (1 - norm(db));
            if (!started) { ctx.moveTo(x, y); started = true; } else { ctx.lineTo(x, y); }
          }
          ctx.stroke();
          ctx.lineTo(W, height);
          ctx.lineTo(0, height);
          ctx.closePath();
          ctx.fillStyle = 'rgba(159,255,216,0.14)';
          ctx.fill();
        }
      }

  // draw theoretical LPF magnitude curve if provided and enabled
  if (eqEnabled && eqLpfEnabled && filter) {
        // sample frequencies across current width
        const N = Math.max(64, Math.floor(W));
        const freq = new Float32Array(N);
        const mag = new Float32Array(N);
        const phase = new Float32Array(N);
        for (let i = 0; i < N; i++) {
          const frac = i / (N - 1);
          const f = logScale
            ? Math.pow(10, Math.log10(fMin) + frac * (Math.log10(fMax) - Math.log10(fMin)))
            : (fMin + frac * (fMax - fMin));
          freq[i] = f;
        }
        try { filter.getFrequencyResponse(freq, mag, phase); } catch {}
        // curve in dB
        ctx.strokeStyle = '#f59e0b'; // amber
        ctx.lineWidth = 2;
        ctx.beginPath();
        let started2 = false;
        let fcX: number | null = null;
        for (let i = 0; i < N; i++) {
          const db = 20 * Math.log10(Math.max(1e-6, mag[i]));
          const x = logScale
            ? ((Math.log10(freq[i]) - Math.log10(fMin)) / (Math.log10(fMax) - Math.log10(fMin))) * W
            : ((freq[i] - fMin) / (fMax - fMin)) * W;
          const y = height * (1 - norm(db));
          if (!started2) { ctx.moveTo(x, y); started2 = true; } else { ctx.lineTo(x, y); }
          // find closest to -3 dB
          if (db <= -3 && fcX == null) { fcX = x; }
        }
        ctx.stroke();
        if (fcX != null) {
          ctx.strokeStyle = 'rgba(245,158,11,0.6)';
          ctx.setLineDash([4, 4]);
          ctx.beginPath(); ctx.moveTo(fcX, 0); ctx.lineTo(fcX, height); ctx.stroke();
          ctx.setLineDash([]);
        }
      }

  // draw theoretical HPF magnitude curve if provided and enabled
  if (eqEnabled && eqHpfEnabled && highpass) {
        const N = Math.max(64, Math.floor(W));
        const freq = new Float32Array(N);
        const mag = new Float32Array(N);
        const phase = new Float32Array(N);
        for (let i = 0; i < N; i++) {
          const frac = i / (N - 1);
          const f = logScale
            ? Math.pow(10, Math.log10(fMin) + frac * (Math.log10(fMax) - Math.log10(fMin)))
            : (fMin + frac * (fMax - fMin));
          freq[i] = f;
        }
        try { highpass.getFrequencyResponse(freq, mag, phase); } catch {}
        ctx.strokeStyle = '#60a5fa'; // blue
        ctx.lineWidth = 2;
        ctx.beginPath();
        let started2 = false;
        let fcX: number | null = null;
        for (let i = 0; i < N; i++) {
          const db = 20 * Math.log10(Math.max(1e-6, mag[i]));
          const x = logScale
            ? ((Math.log10(freq[i]) - Math.log10(fMin)) / (Math.log10(fMax) - Math.log10(fMin))) * W
            : ((freq[i] - fMin) / (fMax - fMin)) * W;
          const y = height * (1 - norm(db));
          if (!started2) { ctx.moveTo(x, y); started2 = true; } else { ctx.lineTo(x, y); }
          if (db >= -3 && fcX == null) { fcX = x; } // crossing upward to -3 dB
        }
        ctx.stroke();
        if (fcX != null) {
          ctx.strokeStyle = 'rgba(96,165,250,0.6)';
          ctx.setLineDash([4, 4]);
          ctx.beginPath(); ctx.moveTo(fcX, 0); ctx.lineTo(fcX, height); ctx.stroke();
          ctx.setLineDash([]);
        }
      }

  // draw HPF cutoff handle and shade lows (only when enabled)
  if (eqEnabled && eqHpfEnabled && typeof hpfHz === 'number' && isFinite(hpfHz)) {
        const hx = freqToX(hpfHz);
        ctx.fillStyle = 'rgba(96,165,250,0.10)';
    ctx.fillRect(0, 0, hx, height);
        ctx.strokeStyle = '#60a5fa';
        ctx.lineWidth = 2;
        ctx.beginPath(); ctx.moveTo(hx + 0.5, 0); ctx.lineTo(hx + 0.5, height); ctx.stroke();
        ctx.fillStyle = '#60a5fa';
        ctx.beginPath(); ctx.arc(hx, Math.min(height - 8, 28), 6, 0, Math.PI * 2); ctx.fill();
      }

  // draw LPF cutoff handle and shade highs (only when enabled)
  if (eqEnabled && eqLpfEnabled && typeof cutoffHz === 'number' && isFinite(cutoffHz)) {
        const x = freqToX(cutoffHz);
        // shaded area for filtered-out highs
        ctx.fillStyle = 'rgba(239,68,68,0.10)'; // red-ish
    ctx.fillRect(x, 0, W - x, height);
        // cutoff line
        ctx.strokeStyle = '#f59e0b'; // amber line
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(x + 0.5, 0);
        ctx.lineTo(x + 0.5, height);
        ctx.stroke();
        // handle circle
        ctx.fillStyle = '#f59e0b';
        ctx.beginPath();
        ctx.arc(x, Math.min(height - 8, 12), 6, 0, Math.PI * 2);
        ctx.fill();
      }

      // after drawing from 'data', post to worker for next frames (transfer buffer for zero-copy)
  if (status !== 'idle' && workerRef.current && !pendingReqRef.current) {
        try {
          pendingReqRef.current = true;
          workerRef.current.postMessage({ type: 'processSpectrum', buffer: data.buffer, sampleRate: analyser.context?.sampleRate || 48000, outLen: W, logScale, smoothing: 0.17 }, [data.buffer]);
          // data.buffer transferred, allocate new next frame
          data = new Float32Array(binCount);
        } catch (e) { pendingReqRef.current = false; }
      }

      rafRef.current = requestAnimationFrame(draw);
    };

    rafRef.current = requestAnimationFrame(draw);
    return () => {
  if (rafRef.current) cancelAnimationFrame(rafRef.current);
      try { workerRef.current?.terminate(); } catch {}
      workerRef.current = null;
    };
  }, [analyser, filter, highpass, eqEnabled, eqHpfEnabled, eqLpfEnabled, width, height, fps, logScale, minDb, maxDb, cutoffHz, hpfHz, status]);

  // Observe container width changes and mark for resize of backing store
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ro = new ResizeObserver(() => { needsResizeRef.current = true; });
    try { ro.observe(canvas); } catch {}
    const onWinResize = () => { needsResizeRef.current = true; };
    window.addEventListener('resize', onWinResize);
    return () => { try { ro.disconnect(); } catch {} window.removeEventListener('resize', onWinResize); };
  }, []);
  const onPointerDown: React.PointerEventHandler<HTMLCanvasElement> = (e) => {
    if (!onCutoffChange && !onHpfChange) return;
    const rect = (e.currentTarget as HTMLCanvasElement).getBoundingClientRect();
    const x = e.clientX - rect.left;
    const canvas = canvasRef.current;
    if (!canvas) return;
    // compute freq mapping using same bounds
    const nyquist = (analyser?.context?.sampleRate || 48000) / 2;
    const fMin = 20; const fMax = nyquist;
    const xToF = (xx: number) => {
      const w = Math.max(1, Math.floor(canvasRef.current?.clientWidth || width));
      const clampedX = Math.max(0, Math.min(w, xx));
      if (!logScale) {
        const frac = clampedX / w; return fMin + frac * (fMax - fMin);
      }
      const lmin = Math.log10(fMin); const lmax = Math.log10(fMax);
      const lf = lmin + (clampedX / w) * (lmax - lmin);
      return Math.pow(10, lf);
    };
    const fToX = (f: number) => {
      const clamped = Math.max(fMin, Math.min(fMax, f));
      const w = Math.max(1, Math.floor(canvasRef.current?.clientWidth || width));
      if (!logScale) return ((clamped - fMin) / (fMax - fMin)) * w;
      const lmin = Math.log10(fMin); const lmax = Math.log10(fMax);
      return ((Math.log10(clamped) - lmin) / (lmax - lmin)) * w;
    };
    // choose nearest handle if both present; otherwise pick whichever exists
    let which: 'lp' | 'hp';
    if (typeof cutoffHz === 'number' && typeof hpfHz === 'number') {
      const distLp = Math.abs(fToX(cutoffHz) - x);
      const distHp = Math.abs(fToX(hpfHz) - x);
      which = distHp < distLp ? 'hp' : 'lp';
    } else if (typeof hpfHz === 'number' && onHpfChange) {
      which = 'hp';
    } else {
      which = 'lp';
    }
  draggingRef.current = which;
  (e.currentTarget as HTMLCanvasElement).setPointerCapture(e.pointerId);
  if (which === 'lp' && onCutoffChange) onCutoffChange(xToF(x));
  if (which === 'hp' && onHpfChange) onHpfChange(xToF(x));
  };

  const onPointerMove: React.PointerEventHandler<HTMLCanvasElement> = (e) => {
    if (!draggingRef.current) return;
    const rect = (e.currentTarget as HTMLCanvasElement).getBoundingClientRect();
    const x = e.clientX - rect.left;
    const nyquist = (analyser?.context?.sampleRate || 48000) / 2;
    const fMin = 20; const fMax = nyquist;
    const lmin = Math.log10(fMin); const lmax = Math.log10(fMax);
    let hz: number;
    const w = Math.max(1, Math.floor(canvasRef.current?.clientWidth || width));
    if (!logScale) {
      const frac = Math.max(0, Math.min(1, x / w));
      hz = fMin + frac * (fMax - fMin);
    } else {
      const frac = Math.max(0, Math.min(1, x / w));
      const lf = lmin + frac * (lmax - lmin);
      hz = Math.pow(10, lf);
    }
  if (draggingRef.current === 'lp' && onCutoffChange) onCutoffChange(hz);
  if (draggingRef.current === 'hp' && onHpfChange) onHpfChange(hz);
  };

  const onPointerUp: React.PointerEventHandler<HTMLCanvasElement> = (e) => {
    if (!draggingRef.current) return;
  draggingRef.current = false;
    try { (e.currentTarget as HTMLCanvasElement).releasePointerCapture(e.pointerId); } catch {}
  };

  return (
    <canvas
      ref={canvasRef}
      className={className}
      style={{ width: '100%', height, touchAction: 'none', cursor: (onCutoffChange || onHpfChange) ? (draggingRef.current ? 'grabbing' : 'crosshair') : 'default' }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerLeave={onPointerUp}
    />
  );
};

export default MasterFrequencyResponse;
